import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { Button, LINK_CLASS, Notice } from '@efira/ui';

import { authClient } from '../auth/client';
import { AuthLayout } from './AuthLayout';

/**
 * «Письмо отправлено».
 *
 * Один и тот же экран показывается и новому адресу, и уже занятому - в этом
 * весь смысл: по поведению интерфейса нельзя определить, зарегистрирована ли
 * почта. Отличается только содержимое письма (см. сервер,
 * sendExistingAccountNotice).
 *
 * Повторная отправка защищена обратным отсчётом: без него кнопку жмут подряд,
 * и пользователь получает пачку одинаковых писем, а сервер - лишний расход.
 */
const RESEND_SECONDS = 60;

export function CheckEmailScreen() {
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email ?? '';

  const [left, setLeft] = useState(RESEND_SECONDS);
  const [sending, setSending] = useState(false);
  const [resent, setResent] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (left <= 0) {
      return;
    }

    timer.current = window.setTimeout(() => {
      setLeft((value) => value - 1);
    }, 1000);

    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
    };
  }, [left]);

  async function resend() {
    if (email === '') {
      return;
    }

    setSending(true);
    await authClient.sendVerificationEmail({
      email,
      callbackURL: `${window.location.origin}/verify-email`,
    });
    setSending(false);
    setResent(true);
    setLeft(RESEND_SECONDS);
  }

  return (
    <AuthLayout
      title="Проверьте почту"
      footer={
        <RouterLink className={LINK_CLASS} to="/login">
          Перейти ко входу
        </RouterLink>
      }
    >
      <Notice tone="success" title="Письмо отправлено">
        {email === '' ? (
          'Откройте письмо и перейдите по ссылке — после этого можно войти.'
        ) : (
          <>
            Мы написали на <b>{email}</b>. Откройте письмо и перейдите по ссылке — после этого можно
            войти.
          </>
        )}
      </Notice>

      {resent ? (
        <div>
          <Notice tone="info" live>
            Письмо отправлено повторно.
          </Notice>
        </div>
      ) : null}

      <div className="mt-2 flex flex-col gap-3">
        <Button
          variant="secondary"
          block
          disabled={left > 0 || email === ''}
          loading={sending}
          onClick={() => {
            void resend();
          }}
        >
          {left > 0 ? `Отправить ещё раз через ${left} с` : 'Отправить ещё раз'}
        </Button>
      </div>

      <p className="text-foreground/75 mt-4 text-sm">
        Письма нет? Загляните в спам. Ссылка действует 24 часа.
      </p>
    </AuthLayout>
  );
}
